import {HCThemeType} from '@hconnect/uikit'
import {makeStyles, Button, Box, useTheme, useMediaQuery, CircularProgress} from '@material-ui/core'
import ArrowRightAlt from '@material-ui/icons/ArrowRightAlt'
import CloseIcon from '@material-ui/icons/Close'
import DateRange from '@material-ui/icons/DateRange'
import EventNoteIcon from '@material-ui/icons/EventNote'
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined'
import {TabContext} from '@material-ui/lab'
import DeleteIcon from '@mui/icons-material/Delete'
import WestIcon from '@mui/icons-material/West'
import {Typography} from '@mui/material'
import classnames from 'classnames'
import React, {useCallback, useEffect, useReducer, useRef, useState} from 'react'
import {FormProvider, FormState, useForm, useFormContext} from 'react-hook-form'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'

import {usePostSlotReservation} from '../../../../common/react-query/hooks/mutations/usePostSlotReservation'
import {Dictionary} from '../../../../common/types'
import {useConfirmation} from '../../../../OrderIntake/components/ConfirmationDialog/ConfirmationService'
import {useFeaturesState} from '../../../../Organisms/Features'
import {useBulkCementOrderIntake} from '../../../../Organisms/OrderIntake/BulkCementOrderIntake.provider'
import {generateGuid, getDefaultMaterialOptions} from '../../../../Organisms/OrderIntake/utils'
import {AppState} from '../../../../Root.store'
import {BulkOrderFormValues} from '../../../BulkOrdersFormValues'
import {ShippingType} from '../../../declarations/OrderIntake.enums'
import {
  ActiveMaterialDeliveryItem,
  ActiveMaterialEditorMode,
  DeliveryTime,
  HaulerInfo,
  OrderIntakeMaterialOptionPayload,
  OrderIntakePayload,
  OrdersFormValidityState,
  SlotSiloConfig,
  TimeSelectorTrackingEventType
} from '../../../declarations/types'
import {OrderWindowStatus, Slot} from '../../containers/slotManagement/types'
import {
  getValidAndInvalidMaterialsForCollect,
  getValidAndInvalidMaterialsForDelivery,
  ResolvedMaterialSettings
} from '../../utils'
import {useSlotRelease} from '../slotManagement/hooks/useSlotRelease'
import {SelectedSplitLoadMaterials} from '../SplitLoad/types'

import {
  ApplyButtonContainer,
  CollapsedTab,
  CollapsedTabs,
  ContentBox,
  MaterialEditorCloseButton,
  TabPanel
} from './components'
import testIds from './dataTestIds'
import {DateTimeTab} from './DateTimeTab'
import {DetailsTab} from './DetailsTab'
import {HaulierInfoTab} from './HaulierInfoTab'
import {PoTab} from './PoTab'
import {MaterialEditorTabs, MaterialOrderForm} from './types'
import {getCurrentTab} from './utils'
import {useResetSlotSiloConfig} from '../slotManagement/hooks/useResetSlotSiloConfig'

const customerPrefix = ' Customer: '

const useStyles = makeStyles((theme: HCThemeType) => {
  const borderBox = `1px solid ${theme.palette.grey[200]}`
  return {
    tabButton: {
      textTransform: 'none'
    },
    applyButtonContainer: {
      backgroundColor: theme.palette.grey[50],
      borderTop: borderBox,
      flexGrow: 0,
      padding: theme.spacing(2),
      position: 'sticky',
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      bottom: -1 * theme.spacing(1),
      '& svg': {
        marginRight: theme.spacing(1)
      }
    },
    closeButtonContainer: {
      alignSelf: 'end',
      padding: 5
    }
  }
})

const getIndicatorClassName = (
  formState: FormState<MaterialOrderForm>,
  path: keyof MaterialOrderForm
) => {
  if (formState.errors[path]) return 'error'
  if (formState.dirtyFields[path]) return 'dirty'
  return undefined
}
const Indicator: React.FC<{state: FormState<MaterialOrderForm>; path: keyof MaterialOrderForm}> = (
  p
) => <span className={classnames('indicator', getIndicatorClassName(p.state, p.path))}></span>

type Props = {
  key: string
  orderPayload: OrderIntakePayload
  index: number // the index of the form
  canDeleteMaterial: boolean
  isCustomerReferenceSame: boolean
  checkValidityBySlots: boolean
  shippingType: ShippingType
  showPoTab: boolean
  activeItem?: ActiveMaterialDeliveryItem
  editorMode?: ActiveMaterialEditorMode
  isSplitLoad?: boolean
  closeActiveItem: () => void
  onDateChange: (date: string) => void
  onTimeChange: (date: DeliveryTime) => void
  onSlotChange?: (slot: DeliveryTime, slotSiloConfig: SlotSiloConfig) => void
  onQuantityChange: (quantity: number) => void
  onMaterialChange: (selectedMaterialOption: OrderIntakeMaterialOptionPayload) => void
  onHaulerInfoChange?: (haulerInfo?: HaulerInfo) => void
  onCustomerReferenceChange?: (customerReference: string) => void
  onDriverInstructionsChange?: (customerReference: string) => void
  onDispatchNotesChange?: (dispatchNotes: string) => void
  savedBomKey?: string
  onBomKeyChange?: (bomKey: string | undefined) => void
  onSplitLoadMaterialsSelected?: (materials: SelectedSplitLoadMaterials) => void
  savedSplitLoadData?: SelectedSplitLoadMaterials
  onChange: (data: MaterialOrderForm) => void
  onTimeSelectorTrack?: (events: Dictionary<number>) => void
  onDeleteMaterial: () => void
  onStatusChange: (hasChanges: boolean) => void
  onFormValidation: (formValidation: OrdersFormValidityState) => void
  setOrderWindowStatus?: (value: OrderWindowStatus) => void
  slots?: Slot[]
  setSlots?: (slots: Slot[]) => void
}

// eslint-disable-next-line complexity
export const MaterialOrderEditor: React.FC<Props> = ({
  index,
  orderPayload,
  canDeleteMaterial,
  activeItem,
  editorMode,
  checkValidityBySlots,
  isSplitLoad,
  closeActiveItem,
  onDateChange,
  onTimeChange,
  onSlotChange,
  onHaulerInfoChange,
  onCustomerReferenceChange,
  onQuantityChange,
  onMaterialChange,
  onDriverInstructionsChange,
  onDispatchNotesChange,
  savedBomKey,
  onBomKeyChange,
  onSplitLoadMaterialsSelected,
  savedSplitLoadData,
  onChange,
  shippingType,
  onTimeSelectorTrack,
  onStatusChange,
  onFormValidation,
  setOrderWindowStatus,
  slots,
  setSlots
}) => {
  const {t} = useTranslation()
  const classes = useStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const {getFeature} = useFeaturesState()
  const confirm = useConfirmation()
  const [activeTab, setActiveTab] = useState(getCurrentTab(activeItem))
  const [activeSelectedDeliveryDate, setActiveSelectedDeliveryDate] = useState<string | undefined>(
    orderPayload.deliveryDate
  )
  const [activeSelectedCustomerReference, setActiveSelectedCustomerReference] = useState<string>(
    orderPayload.customerReference
  )
  const [selectedDeliveryTimeSlot, setSelectedDeliveryTimeSlot] = useState<DeliveryTime>()
  const [selectedSlotSiloConfig, setSelectedSlotSiloConfig] = useState<SlotSiloConfig>({
    loadingStation: '',
    siloNumber: '',
    externalReference: ''
  })

  const showHaulierInfoWithoutPhoneNumber = getFeature(
    'OrderIntakeHaulierInformationWithoutPhoneNumber'
  )
  const showHaulierInfo =
    getFeature('OrderIntakeHaulierInformation') ||
    getFeature('OrderIntakeHaulierInformationWithoutPhoneNumber')

  useEffect(() => {
    setActiveTab(getCurrentTab(activeItem))
    setActiveSelectedDeliveryDate(parentForm.watch(`orders.${index}.payload.deliveryDate`))
    setSelectedDeliveryTimeSlot(parentForm.watch(`orders.${index}.payload.deliveryTime`))
    setSelectedSlotSiloConfig(parentForm.watch(`orders.${index}.payload.slot`))
    setActiveSelectedCustomerReference(
      parentForm.watch(`orders.${index}.payload.customerReference`)
    )
  }, [activeItem])

  const [tabValidationState, setTabValidationState] = useState({
    [MaterialEditorTabs.Details]: true,
    [MaterialEditorTabs.Date]: true,
    [MaterialEditorTabs.Haulier]: true
  })

  const parentForm = useFormContext<BulkOrderFormValues>()
  const orderPath = `orders.${index}` as const
  const selectedSite = parentForm.watch('selectedSite')
  const currentCustomerReferenceValue = parentForm.watch(
    `orders.${index}.payload.customerReference`
  )

  const order = parentForm.getValues(orderPath)
  const defaultMaterialOption = getDefaultMaterialOptions(selectedSite.materials, orderPayload)
  const selectedMaterial = parentForm.watch(`selectedMaterial.${index}`) ?? defaultMaterialOption

  const [timeSelectorEvents, setTimeSelectorEvents] = useState<Dictionary<number>>()
  const [defaultDriverInstructions, setDefaultDriverInstructions] = useState<string>(
    parentForm.watch(`orders.${index}.payload.additionalDriverInfo.driverInstructions`) || ''
  )
  const [defaultDispatcherNotes, setDefaultDispatcherNotesState] = useState<string>(
    parentForm.watch(`orders.${index}.payload.orderAllocatorInstructions`) || ''
  )

  // save dispatcher notes to parent form immediately
  const setDefaultDispatcherNotes = useCallback(
    (notes: string) => {
      setDefaultDispatcherNotesState(notes)
      onDispatchNotesChange?.(notes)
    },
    [onDispatchNotesChange]
  )

  const isDateClearedByMaterialChangeRef = useRef<boolean>(false)

  const methods = useForm<MaterialOrderForm>({
    mode: 'onChange',
    defaultValues: {
      dateTime: {
        deliveryDate: parentForm.watch(`orders.${index}.payload.deliveryDate`),
        deliveryTime: orderPayload.deliveryTime
      },
      haulerInfo: {
        driverName: parentForm.watch(`orders.${index}.payload.haulerInfo.driverName`),
        driverPhoneNumber: parentForm.watch(`orders.${index}.payload.haulerInfo.driverPhoneNumber`),
        trailerLicensePlate: parentForm.watch(
          `orders.${index}.payload.haulerInfo.trailerLicensePlate`
        ),
        truckLicensePlate: parentForm.watch(`orders.${index}.payload.haulerInfo.truckLicensePlate`)
      },
      details: {
        customerReference: parentForm.watch(`orders.${index}.payload.customerReference`),
        quantity: orderPayload.capacity.quantity,
        driverInstructions: orderPayload.additionalDriverInfo?.driverInstructions,
        selectedMaterialOption: selectedMaterial,
        selectedSplitLoadMaterialsOptions: savedSplitLoadData
      }
    }
  })

  // handle slot reservation when user clicks on finish and Save for IT
  const [selectedSlot, setSelectedSlot] = useState<Slot>({
    materialNumber: '',
    slotDurationMinutes: '',
    loadingStation: '',
    siloNumber: '',
    id: '',
    plantId: '',
    date: '',
    slotConfig: {
      slotStart: '',
      slotEnd: '',
      isAlwaysFull: false,
      slotPosition: 0
    },
    slotStatus: {
      statusName: '',
      isBlockingDelivery: false
    },
    serviceResponseCode: ''
  })
  const {mutate: postSlotReservation, isLoading, isError} = usePostSlotReservation()
  const userCountry = useSelector((state: AppState) => state.userProfile.userProfile?.country || '')
  const [externalReference] = useState(
    `SLOT-EXT-REF-${generateGuid().replace(/-/g, '').slice(0, 4)}`
  ) // this needs to be properly handled on the backend or find a better solution later

  const handleSlotReservation = () => {
    const payload = {
      country: userCountry,
      plantId: selectedSlot.plantId,
      materialNumber: selectedSlot.materialNumber,
      bookingDate: deliveryDate,
      loadingStation: selectedSlot.loadingStation,
      siloNumber: selectedSlot.siloNumber,
      bookedTimeFrom: selectedSlot.slotConfig.slotStart,
      bookedTimeTo: selectedSlot.slotConfig.slotEnd,
      slotDurationMinutes: selectedSlot?.slotDurationMinutes,
      externalReference,
      applicationId: 'hub',
      userId: ''
    }
    postSlotReservation(
      {...payload},
      {
        onSuccess: () => {
          submit()
          setOrderWindowStatus?.(OrderWindowStatus.Reserved)
        },
        onError: (error) => {
          console.error('error while reserving slot: ', error)
        }
      }
    )
  }

  // handle slot release when user changes material after reserving a slot
  const {releaseSlot} = useSlotRelease()
  const {resetSlotSiloConfig} = useResetSlotSiloConfig()

  const handleReservationOrReleaseSlot = () => {
    const slotExternalReference =
      parentForm.getValues().orders[index].payload.slot.externalReference

    const isDeliveryTimeDirty = methods.formState.dirtyFields.dateTime?.deliveryTime
    const hasMaterialChanged = methods.formState.dirtyFields.details?.selectedMaterialOption
    const hasSplitLoadMaterialsChanged =
      methods.formState.dirtyFields.details?.selectedSplitLoadMaterialsOptions

    // Only release the old slot if:
    // 1. User selected a new time slot (isDeliveryTimeDirty)
    // 2. Material/plant have NOT changed (release is handled by useResetDeliveryDateTime when material changes)
    // 3. There's an existing slot to release (slotExternalReference exists)
    const shouldReleaseSlot =
      isDeliveryTimeDirty &&
      !hasMaterialChanged &&
      !hasSplitLoadMaterialsChanged &&
      slotExternalReference

    if (shouldReleaseSlot) {
      const previousSlot = parentForm.getValues().orders[index].payload.slot
      releaseSlot({
        externalReference: slotExternalReference,
        plantId: previousSlot.plantId ?? selectedMaterial.plant.plantNumber,
        materialNumber: previousSlot.materialNumber ?? selectedMaterial.material.materialNumber,
        onSuccess: () => {
          resetSlotSiloConfig(parentForm, index)
        }
      })
    }

    // reserve slot if user picks a new slot else continue to submit
    isDeliveryTimeDirty ? handleSlotReservation() : submit()
  }

  const defaultMaterial = selectedSite.materials[orderPayload.materialEnteredNumber][0]
  const [isItemOpen, hideItem] = useReducer(() => false, true)

  const customerReference = methods.getValues().details.customerReference
  const slotDeliveryTime = methods.getValues().dateTime.deliveryTime

  const isAllTabsValid = Object.values(tabValidationState).every((tab) => tab)

  const getDateTimeError = () => {
    return getIndicatorClassName(methods.formState, 'dateTime') === 'error'
  }

  const hasSplitLoadMaterials = React.useMemo(() => {
    if (!isSplitLoad) return true
    return savedSplitLoadData?.materials && savedSplitLoadData.materials.length >= 2
  }, [isSplitLoad, savedSplitLoadData])

  // Sync savedSplitLoadData prop to form state when it changes
  useEffect(() => {
    if (savedSplitLoadData) {
      methods.setValue('details.selectedSplitLoadMaterialsOptions', savedSplitLoadData)
    }
  }, [savedSplitLoadData])

  const isFormInvalid =
    !methods.formState.isValid ||
    getDateTimeError() ||
    getIndicatorClassName(methods.formState, 'details') === 'error' ||
    getIndicatorClassName(methods.formState, 'haulerInfo') === 'error' ||
    getIndicatorClassName(methods.formState, 'dateTime') === 'error' ||
    !isAllTabsValid ||
    isLoading ||
    !hasSplitLoadMaterials

  const isCurrentTabInvalid = (() => {
    switch (activeTab) {
      case MaterialEditorTabs.Details:
        return (
          getIndicatorClassName(methods.formState, 'details') === 'error' || !hasSplitLoadMaterials
        )
      case MaterialEditorTabs.Date:
        return getDateTimeError()
      case MaterialEditorTabs.Haulier:
        return getIndicatorClassName(methods.formState, 'haulerInfo') === 'error'
      default:
        return false
    }
  })()

  const isLastTab =
    editorMode !== 'add' ||
    activeTab === MaterialEditorTabs.Haulier ||
    (activeTab === MaterialEditorTabs.Date &&
      !(
        shippingType === ShippingType.COLLECT &&
        (getFeature('OrderIntakeHaulierInformation') ||
          getFeature('OrderIntakeHaulierInformationWithoutPhoneNumber'))
      ))

  const isNextOrSubmitDisabled = isLastTab ? isFormInvalid : isCurrentTabInvalid

  useEffect(() => {
    const validateAllTabs = async () => {
      const isValid = await methods.trigger()
      setTabValidationState((prevState) => ({
        ...prevState,
        [activeTab]: isValid
      }))
    }

    void validateAllTabs()
  }, [activeTab, customerReference, slotDeliveryTime, methods])

  useEffect(() => {
    onFormValidation({
      isFormInvalid: isFormInvalid,
      index
    })
  }, [currentCustomerReferenceValue, index, isFormInvalid, onFormValidation])

  useEffect(() => {
    hideItem()

    const driverInstructions = orderPayload.additionalDriverInfo?.driverInstructions
    const readOnlyDriversInstructions = selectedSite?.driverInstructions ?? ''

    const isReadOnlyDriversInstructionsAvailable = readOnlyDriversInstructions
      ? readOnlyDriversInstructions.length > 0
      : false

    let resolvedDriverInstructions = driverInstructions

    if (isReadOnlyDriversInstructionsAvailable) {
      resolvedDriverInstructions = driverInstructions?.replace(
        `${readOnlyDriversInstructions}${customerPrefix}`,
        ''
      )
    }
    methods.setValue('details.driverInstructions', resolvedDriverInstructions ?? '')
  }, [])

  React.useEffect(() => {
    const subscription = methods.watch((value, {type}) => {
      if (type === 'change') {
        onStatusChange && onStatusChange(true)
      }
    })
    return () => subscription.unsubscribe()
  }, [methods.watch])

  const {slotConfiguration} = useBulkCementOrderIntake()

  // eslint-disable-next-line complexity
  const submit = methods.handleSubmit((data) => {
    closeActiveItem()

    const driverInstructions = data.details.driverInstructions
    const readOnlyDriversInstructions = selectedSite?.driverInstructions ?? ''
    const isReadOnlyDriversInstructionsAvailable = readOnlyDriversInstructions
      ? readOnlyDriversInstructions.length > 0
      : false
    const isSlotManagedCollect =
      shippingType === ShippingType.COLLECT &&
      getFeature('orderIntakeSlotManagedPlantLaneAvailability')
    // to call more fieldArray methods one after another is problem. we should do it at once.
    // needs rework when more are handled by single submit
    if (methods.formState.dirtyFields.dateTime?.deliveryTime) {
      onTimeChange(data.dateTime.deliveryTime)
    }
    if (methods.formState.dirtyFields.dateTime?.deliveryTime) {
      onSlotChange && onSlotChange(data.dateTime.deliveryTime, selectedSlotSiloConfig)
    }

    // For slot-managed: always sync date if child form has a value (handles reset + re-select scenario)
    // For other flows: only sync if date was changed (dirty)
    const shouldSyncDate = isSlotManagedCollect
      ? Boolean(deliveryDate)
      : methods.formState.dirtyFields.dateTime?.deliveryDate

    if (shouldSyncDate) {
      onDateChange(data.dateTime.deliveryDate)
    }

    if (methods.formState.dirtyFields.haulerInfo) {
      onHaulerInfoChange && onHaulerInfoChange(data.haulerInfo)
    }

    if (methods.formState.dirtyFields.details?.customerReference) {
      onCustomerReferenceChange && onCustomerReferenceChange(data.details.customerReference)
    }

    if (methods.formState.dirtyFields.details?.quantity) {
      onQuantityChange && onQuantityChange(data.details.quantity)
    }

    if (
      methods.formState.dirtyFields.details?.driverInstructions ||
      isReadOnlyDriversInstructionsAvailable
    ) {
      let resolvedDriverInstructions = isReadOnlyDriversInstructionsAvailable
        ? driverInstructions
          ? `${readOnlyDriversInstructions}${customerPrefix}${driverInstructions}`
          : readOnlyDriversInstructions
        : driverInstructions

      if (!isReadOnlyDriversInstructionsAvailable && !driverInstructions) {
        resolvedDriverInstructions = ''
      }

      onDriverInstructionsChange && onDriverInstructionsChange(resolvedDriverInstructions)
    }

    if (methods.formState.dirtyFields.details?.selectedMaterialOption) {
      data.details.selectedMaterialOption.customerReference = data.details.customerReference
      onMaterialChange && onMaterialChange(data.details.selectedMaterialOption)
    }

    if (
      methods.formState.dirtyFields.details?.selectedSplitLoadMaterialsOptions &&
      data.details.selectedSplitLoadMaterialsOptions
    ) {
      onSplitLoadMaterialsSelected &&
        onSplitLoadMaterialsSelected(data.details.selectedSplitLoadMaterialsOptions)
    }

    if (methods.formState.dirtyFields.details?.dispatcherNotes) {
      onDispatchNotesChange && onDispatchNotesChange(data.details.dispatcherNotes)
    }

    onTimeSelectorTrack && timeSelectorEvents && onTimeSelectorTrack(timeSelectorEvents)
    onChange(data)
  })

  const handleCloseActiveItem = async () => {
    void (await methods.trigger())

    onFormValidation({
      isFormInvalid: currentCustomerReferenceValue === '',
      index
    })

    if (methods.formState.isDirty) {
      void confirm({
        'data-test-id': testIds.confirmDialog,
        title: t('orderIntake.materialOrder.confirmDialog.title'),
        submitButtonTitle: t('orderIntake.materialOrder.confirmDialog.submitButton'),
        cancelButtonTitle: t('orderIntake.materialOrder.confirmDialog.cancelButton'),
        variant: 'discard'
      }).then(() => {
        closeActiveItem()
      })
    } else {
      closeActiveItem()
    }
  }

  const timeSelectorTrack = (eventType: TimeSelectorTrackingEventType) => {
    const eventsCopy = {...timeSelectorEvents}
    const count = eventsCopy[eventType] ?? 0
    eventsCopy[eventType] = count + 1

    setTimeSelectorEvents(eventsCopy)
  }

  const handleBack = () => {
    switch (activeTab) {
      case MaterialEditorTabs.Date:
        setActiveTab(MaterialEditorTabs.Details)
        break
      case MaterialEditorTabs.Haulier:
        setActiveTab(MaterialEditorTabs.Date)
        break
    }
  }

  const handleNextOrSubmit = () => {
    if (editorMode === 'add') {
      const isCollect = shippingType === ShippingType.COLLECT
      const hasSlotManagedReservation =
        isCollect &&
        getFeature('orderIntakeSlotManagedPlantLaneAvailability') &&
        slots?.[0]?.serviceResponseCode === 'RC0'

      switch (activeTab) {
        case MaterialEditorTabs.Details:
          setActiveTab(MaterialEditorTabs.Date)
          break
        case MaterialEditorTabs.Date:
          if (
            isCollect &&
            (getFeature('OrderIntakeHaulierInformation') ||
              getFeature('OrderIntakeHaulierInformationWithoutPhoneNumber'))
          ) {
            setActiveTab(MaterialEditorTabs.Haulier)
          } else if (hasSlotManagedReservation) {
            handleReservationOrReleaseSlot()
          } else {
            submit()
          }
          break
        case MaterialEditorTabs.Haulier:
          submit()
          break
      }
    } else {
      submit()
    }
  }

  const deliveryDate = methods.watch('dateTime.deliveryDate')
  const currentSelectedMaterialOption = methods.watch('details.selectedMaterialOption')
  const materialEnteredNumber =
    currentSelectedMaterialOption?.material.materialEnteredNumber ??
    defaultMaterial.material.materialEnteredNumber
  const materialPlantName =
    currentSelectedMaterialOption?.plant.plantName ?? defaultMaterial.plant.plantName

  const shouldFlaggedMaterialsBeFiltered = getFeature('OrderIntakeRemoveIrrelevantMaterials')
  const materialOptions = Object.values(selectedSite.materials).flatMap((m) => m)

  const [resolvedMaterialSettings, setResolvedMaterialSettings] =
    useState<ResolvedMaterialSettings>()

  useEffect(() => {
    if (isDateClearedByMaterialChangeRef.current) {
      isDateClearedByMaterialChangeRef.current = false
      return
    }
    const materialSettings =
      shippingType === ShippingType.DELIVER
        ? getValidAndInvalidMaterialsForDelivery(
            materialOptions,
            selectedSite.materials,
            materialEnteredNumber,
            deliveryDate,
            materialPlantName,
            checkValidityBySlots,
            slotConfiguration,
            shouldFlaggedMaterialsBeFiltered
          )
        : getValidAndInvalidMaterialsForCollect(
            selectedSite.materials,
            currentSelectedMaterialOption ?? selectedMaterial,
            materialEnteredNumber,
            deliveryDate,
            shouldFlaggedMaterialsBeFiltered
          )

    setResolvedMaterialSettings(materialSettings)

    if (materialSettings.materialOption)
      methods.setValue('details.selectedMaterialOption', materialSettings.materialOption)
  }, [deliveryDate])

  const getApplyButtonLabel = () => {
    if (editorMode === 'add') {
      switch (activeTab) {
        case MaterialEditorTabs.Details:
          return t('orderIntake.materialOrder.addNextDateTime')
        case MaterialEditorTabs.Date:
          return shippingType === ShippingType.COLLECT &&
            (getFeature('OrderIntakeHaulierInformation') ||
              getFeature('OrderIntakeHaulierInformationWithoutPhoneNumber'))
            ? t('orderIntake.materialOrder.addHaulierInfo')
            : t('orderIntake.materialOrder.finishSave')
        case MaterialEditorTabs.Haulier:
          return t('orderIntake.materialOrder.finishSave')
      }
    }

    return t('orderIntake.materialOrder.apply')
  }

  if (!order) return null

  const handleBomChange = (bom?: {
    bomMaterialNumber?: string
    bomMaterialPositionNumber?: string
  }) => {
    const numberPath = `orders.${index}.payload.bomMaterialNumber` as const
    const positionPath = `orders.${index}.payload.bomMaterialPositionNumber` as const
    const numberVal = bom?.bomMaterialNumber ?? ''
    const positionVal = bom?.bomMaterialPositionNumber ?? ''

    parentForm.setValue(numberPath, numberVal, {shouldDirty: true})
    parentForm.setValue(positionPath, positionVal, {shouldDirty: true})
  }

  const handleTabChange = async (event, newValue) => {
    const isValid = await methods.trigger()

    if (isValid) {
      setActiveTab(newValue as MaterialEditorTabs)
    } else {
      await methods.trigger()
    }
  }

  return (
    <form onSubmit={submit}>
      <FormProvider {...methods}>
        <TabContext value={activeTab}>
          <CollapsedTabs
            style={!isMobile ? {borderBottom: 'none'} : {}}
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            orientation="horizontal"
          >
            <CollapsedTab
              value={MaterialEditorTabs.Details}
              selected={activeTab === MaterialEditorTabs.Details}
              data-test-id={testIds.tabs.details}
              label={
                <Typography
                  className={classes.tabButton}
                  variant="body2"
                  color={activeTab === MaterialEditorTabs.Details ? 'primary' : 'text.primary'}
                >
                  <EventNoteIcon /> {t('orderIntake.materialOrder.details')}
                  <Indicator state={methods.formState} path="details" />
                </Typography>
              }
            />

            <CollapsedTab
              value={MaterialEditorTabs.Date}
              selected={activeTab === MaterialEditorTabs.Date}
              disabled={isFormInvalid}
              data-test-id={testIds.tabs.dateTime}
              label={
                <Typography
                  className={classes.tabButton}
                  variant="body2"
                  color={activeTab === MaterialEditorTabs.Date ? 'primary' : 'text.primary'}
                >
                  <DateRange /> {t('orderIntake.materialOrder.dateTime')}
                  <Indicator state={methods.formState} path="dateTime" />
                </Typography>
              }
            />
            {shippingType === ShippingType.COLLECT && showHaulierInfo && (
              <CollapsedTab
                value={MaterialEditorTabs.Haulier}
                selected={activeTab === MaterialEditorTabs.Haulier}
                data-test-id={testIds.tabs.haulier}
                label={
                  <Typography
                    className={classes.tabButton}
                    variant="body2"
                    color={activeTab === MaterialEditorTabs.Haulier ? 'primary' : 'text.primary'}
                  >
                    <LocalShippingOutlinedIcon /> {t('orderIntake.materialOrder.haulierInfo')}
                    <Indicator state={methods.formState} path="haulerInfo" />
                  </Typography>
                }
              />
            )}
          </CollapsedTabs>
          <ContentBox style={{border: 'none'}}>
            <MaterialEditorCloseButton
              data-test-id={testIds.closeBtn}
              aria-label="Close"
              onClick={handleCloseActiveItem}
            >
              <CloseIcon />
            </MaterialEditorCloseButton>

            <TabPanel value={MaterialEditorTabs.Details} data-test-id={testIds.tabPanel.details}>
              <DetailsTab
                selectedSite={selectedSite ?? []}
                validMaterialOptions={resolvedMaterialSettings?.validMaterialOptions ?? []}
                invalidMaterialOptions={resolvedMaterialSettings?.invalidMaterialOptions ?? []}
                isTimeForOrderOver={parentForm.watch('isTimeForOrderOver')}
                index={index}
                slotConfigurations={slotConfiguration}
                orderRequest={order}
                shippingType={shippingType}
                defaultMaterialOption={defaultMaterial}
                onTimeSelectorTrack={timeSelectorTrack}
                defaultDriverInstructions={defaultDriverInstructions}
                setDefaultDriverInstructions={setDefaultDriverInstructions}
                activeSelectedCustomerReference={activeSelectedCustomerReference}
                setActiveSelectedCustomerReference={setActiveSelectedCustomerReference}
                onBomChange={handleBomChange}
                savedBomKey={savedBomKey}
                setSavedBomKey={onBomKeyChange}
                onSplitLoadMaterialsSelected={onSplitLoadMaterialsSelected}
                savedSplitLoadData={savedSplitLoadData}
                isSplitLoad={isSplitLoad}
                defaultDispatcherNotes={defaultDispatcherNotes}
                setDefaultDispatcherNotes={setDefaultDispatcherNotes}
                setActiveSelectedDeliveryDate={setActiveSelectedDeliveryDate}
                parentForm={parentForm}
                isDateClearedByMaterialChangeRef={isDateClearedByMaterialChangeRef}
              />
            </TabPanel>
            <TabPanel value={MaterialEditorTabs.Date} data-test-id={testIds.tabPanel.dateTime}>
              <DateTimeTab
                selectedSite={selectedSite}
                slotConfigurations={slotConfiguration}
                order={order}
                shippingType={shippingType}
                initiallyOpenedItem={isItemOpen ? activeItem : undefined}
                defaultMaterialOption={defaultMaterial}
                onTimeSelectorTrack={timeSelectorTrack}
                activeSelectedDeliveryDate={activeSelectedDeliveryDate}
                setActiveSelectedDeliveryDate={setActiveSelectedDeliveryDate}
                setSelectedDeliveryTimeSlot={setSelectedDeliveryTimeSlot}
                selectedDeliveryTimeSlot={selectedDeliveryTimeSlot}
                setSelectedSlotSiloConfig={setSelectedSlotSiloConfig}
                setSelectedSlot={setSelectedSlot}
                isError={isError}
                externalReference={externalReference}
                slots={slots ?? []}
                setSlots={setSlots}
              />
            </TabPanel>
            <TabPanel value={MaterialEditorTabs.Haulier} data-test-id={testIds.tabPanel.haulier}>
              <HaulierInfoTab
                showHaulierInfoWithoutPhoneNumber={showHaulierInfoWithoutPhoneNumber}
              />
            </TabPanel>
            <TabPanel value={MaterialEditorTabs.Po} data-test-id={testIds.tabPanel.po}>
              <PoTab />
            </TabPanel>

            <ApplyButtonContainer>
              <Box display="flex" flexDirection="row" gridColumnGap={10}>
                {activeTab !== MaterialEditorTabs.Details && (
                  <Button data-test-id={testIds.backBtn} onClick={handleBack}>
                    <WestIcon />
                    {t('orderIntake.materialOrder.back')}
                  </Button>
                )}

                <Button
                  data-test-id={testIds.submitBtn}
                  color="primary"
                  style={isNextOrSubmitDisabled ? {background: '#B2C3C9'} : {background: '#29AAFF'}}
                  fullWidth
                  disabled={isNextOrSubmitDisabled}
                  onClick={handleNextOrSubmit}
                >
                  <ArrowRightAlt />
                  {getApplyButtonLabel()}
                  {isLoading && (
                    <CircularProgress
                      size={30}
                      color="inherit"
                      style={{
                        marginLeft: '8px'
                      }}
                    />
                  )}
                </Button>
              </Box>

              {canDeleteMaterial && (
                <Button
                  data-test-id={testIds.deleteBtn}
                  fullWidth
                  onClick={handleCloseActiveItem}
                  style={{color: 'red'}}
                  disabled={!methods.formState.isValid || getDateTimeError()}
                >
                  <DeleteIcon sx={{color: 'red'}} />
                  {t('orderIntake.materialOrder.discardChanges')}
                </Button>
              )}
            </ApplyButtonContainer>
          </ContentBox>
        </TabContext>
      </FormProvider>
    </form>
  )
}
